import { ShippingType } from '../../core/settings';
import { BaseModel, PostalAddress } from '../../global/models';
import { OrderPaymentStatus, PaymentServiceType, PaymentType } from '../payment';
import { UserModelRefDisplayName } from '../user';

// ============================================================================
// References
// ============================================================================

/** Lightweight reference to an Order document. */
export interface OrderRef {
  id: string;
  number: number;
}

// ============================================================================
// Tracking & Shipping value types
// ============================================================================

/** Internal status of a shipment as resolved from the courier tracking. */
export enum ShippingInternalStatus {
  DELIVERED = 'delivered',
  ONDELIVERY = 'ondelivery',
  RETURNED = 'returned',
  TRIED = 'tried',
  INPROGRESS = 'inprogress',
  PICKUP = 'pickup',
}

/** Public-facing tracking snapshot exposed to the client UI. */
export interface TrackingData {
  minDeliveryDate: Date;
  maxDeliveryDate: Date;
  internalStatus: ShippingInternalStatus;
  url: string;
}

/** Estimated delivery window stored on the order. */
export interface ShippingEstimatedDelivery {
  minDeliveryDate: Date;
  maxDeliveryDate: Date;
}

// ============================================================================
// Audit
// ============================================================================

/** Origin of an update operation applied to a document. */
export enum UpdatedByFrom {
  APP = 'app',
  ADMIN = 'admin',
  SERVER = 'server',
}

/** Information about who/what last updated a document. */
export interface UpdatedBy {
  id: string;
  displayName: string;
  from: UpdatedByFrom;
}

// ============================================================================
// Production / printing
// ============================================================================

/** Aggregated print stats for an order. */
export interface PrintStats {
  bnPct: number;
  colorPct: number;
  bnSides: number;
  colorSides: number;
}

/** Per-bundle production status of a print group. */
export interface PrintStatusGroup {
  bundleId?: string;
  pageSize?: string;
  paperWeight?: string;
  color?: string;
  sheetCount?: number;
  sideCount?: number;
  status?: OrderGroupPrintStatus;
}

export enum OrderGroupPrintStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  ERROR = 'error',
  FINISHED = 'finished',
}

export enum OrderPrintableStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  READY = 'ready',
  DISCARDED = 'discarded',
}

// ============================================================================
// Order enums
// ============================================================================

export enum OrderType {
  REGULAR = 'regular',
  COMPLEMENTARY = 'complementary',
}

export enum OrderProcessStatus {
  PENDING = 'pending',          // order is still pending to be processed
  PROCESSING = 'processing',    // order is being processed
  COMPLETE = 'complete',        // order is complete and ready to be dispatched
  CANCELED = 'canceled',        // order has been canceled and won't be processed
  SHIPPED = 'shipped',          // Enviado
  RETURNED = 'returned',        // Devuelto
  PAUSED = 'paused',            // En Espera
  ONHOLD = 'onhold',            // paused by client
  INCIDENCE = 'incidence',      // order has an incidence to be solved prior to shipping
}

/**
 * Production status. Unified across the ionic app and the admin panel
 * (admin contributes FIXING/RESOLVED/FINISHING/FINISHED, ionic contributes
 * REPRINTING/PACKED).
 */
export enum OrderProductionStatus {
  PENDING = 'pending', // order is still pending to be printed
  REPRINTING = 'reprinting', // order is being reprinted
  FIXING = 'fixing',
  RESOLVED = 'resolved',
  PRINTING = 'printing', // order is being printed
  PRINTED = 'printed', // order has been fully printed
  CLASSIFYING = 'classifying', // order is being classified
  CLASSIFYING_F = 'classifying_f', // order is being classified (f)
  CLASSIFYING_PPL = 'classifying_pl', // order is being classified (ppl)
  CLASSIFIED = 'classified', // order has been classified
  CLASSIFIED_F = 'classified_f',
  CLASSIFIED_PPL = 'classified_pl',
  REVIEW = 'review', // order is being reviewed
  PACKED = 'packed', // order is being packed
  FINISHING = 'finishing',
  FINISHED = 'finished',
}

export enum OrderShippingStatus {
  PENDING = 'pending', // package still pending to be shipped
  DELIVERED = 'delivered', // package has been delivered to client
  SHIPPED = 'shipped',
}

/** Used by status pipes to select the matching enum dictionary. */
export enum OrderTypeStatus {
  PROCESS = 'processStatus',
  SHIPPING = 'shippingStatus',
  PAYMENT = 'paymentStatus',
  PRODUCTION = 'productionStatus',
  PAYTYPE = 'paymentType',
  PIGTRANSTYPE = 'piggyTransactionType',
  PIGTRANSSTATUS = 'piggyTransactionStatus',
  PIGTRANSOPT = 'piggyTransactionOption',
}

export enum OrderPriority {
  URGENT1 = 60,
  URGENT2 = 50,
  HIGH1 = 45,
  HIGH2 = 40,
  HIGH3 = 35,
  REGULAR = 30,
  LOW = 20,
}

// ============================================================================
// Order (collection: orders)
// ============================================================================

/**
 * Order document.
 *
 * Unified shape merged from the ionic app (client) and the admin panel
 * (operations) projects. Fields used only by one side are declared optional.
 *
 * Extends {@link BaseModel} (provides `id`, `createdAt`, `updatedAt`,
 * `_version`) so those fields are not redeclared here.
 */
export interface Order extends BaseModel {
  // --- versioning / audit ---
  _appVersion?: string;
  updatedBy?: UpdatedBy;

  // --- identification ---
  number: number; // order number. this needs to be got from settings.nextOrderNumber
  name?: string;
  type: OrderType;
  linkedOrders?: OrderRef[];

  // --- customer ---
  userId?: string; // reference to user document. may be anonymous
  email: string; // user's email address
  phone?: string; // user's phone
  isBusiness?: boolean;

  // --- cart ---
  /**
   * Snapshot of the shopping cart that produced this order.
   * Typed loosely on purpose: the rich `ShoppingCart` model lives in the app
   * layer and depends on cross-domain types; consumers should narrow this
   * value to their own `ShoppingCart` type.
   */
  cart: unknown;

  // --- statuses ---
  paymentStatus: OrderPaymentStatus;
  processStatus: OrderProcessStatus;
  productionStatus?: OrderProductionStatus; // added by server now, DO NOT SET THIS FROM CLIENT APP
  shippingStatus: OrderShippingStatus;
  printableStatus?: OrderPrintableStatus;
  printableStatusErrored?: boolean;

  // --- addresses ---
  shippingAddress: PostalAddress;
  billingAddress: PostalAddress;
  /** Pickup point address selected by the customer. */
  pickupAddress?: unknown;

  // --- shipping ---
  shippingType: ShippingType;
  shippingLabelId?: string;
  shippingNotified?: boolean;
  shippingEstimatedDelivery?: ShippingEstimatedDelivery;
  shippingCourier?: string;
  shippingCourierService?: string;

  // --- payment ---
  paymentType: PaymentType;
  paymentService?: PaymentServiceType; // only if credit card, service used
  paymentRef?: string; // payment reference from service or according to payment type
  paymentLog?: unknown[]; // push payment response here (array so failed + retry success can be tracked)
  paymentAuthCode?: string;
  transactionId?: string; // reference to payment transaction // NEW 2
  refundedAmt?: number; // amount refunded to user (if any)

  // --- comments / acceptance ---
  userComments?: string; // by user on payment
  adminComments?: string; // by admin from panel
  commentCount?: number;
  acceptance?: unknown;
  policy?: boolean;
  ads?: boolean;
  hasReportIncident?: boolean; // boolean that indicates if the client has sent a report incident
  blockSharing?: boolean;
  printProtected?: boolean;

  // --- invoicing ---
  invoiceRef?: string; // invoice reference accotding to API
  invoiceUrl?: string;
  invoiceId?: string;

  // --- responsibles (admin) ---
  responsible?: UserModelRefDisplayName; // admin user responsible of processing this order  // NEW 2
  responsibleFinishing?: UserModelRefDisplayName;

  // --- production tracking (admin) ---
  printGroupCount?: number;
  printStatusGroups?: PrintStatusGroup[];
  printStats?: PrintStats;
  workStations?: string[];
  laminatedCoverCount?: number;
  workOrderId?: string;
  needsWorkOrder?: boolean;

  // --- prioritisation (admin) ---
  priority?: OrderPriority;
  priorityReason?: string;
  urgentForced?: boolean;

  // --- misc (admin) ---
  contains?: string[];
  stockRollback?: boolean;
}

/**
 * Draft order persisted under `users/{id}/draftOrders`.
 *
 * Shares the exact same shape as {@link Order}; declared as a type alias so
 * both stay structurally identical while allowing semantic differentiation
 * at call sites.
 */
export type DraftOrder = Order;
